/**
 * The Deal Score callout A/B test on the product page.
 *
 * Three arms, split by the browser's anonymous id: `none` shows nothing (the
 * control), `badge` shows "% below market + $ saved", `gauge` shows the same
 * facts on an illustrated dial. Only listings with a deal score above 0 and a
 * medium or high confidence estimate take part, in every arm, so the control
 * is the same population as the treatments.
 */

import { z } from 'zod';

export const DEAL_CALLOUT_ARMS = ['none', 'badge', 'gauge'] as const;
export const DealCalloutArmSchema = z.enum(DEAL_CALLOUT_ARMS);
export type DealCalloutArm = z.infer<typeof DealCalloutArmSchema>;

/** What a callout says. Dollars are rounded for display by the component. */
export const DealCalloutSchema = z.object({
  /** utils/deal-score.ts, 1-100. */
  score: z.number().int().min(1).max(100),
  /** Whole percent below the estimate, at least 1. */
  percentBelow: z.number().int().min(1).max(100),
  /** Dollars below the estimate. */
  savings: z.number().positive(),
  /** The estimate the price was measured against, in dollars. */
  reference: z.number().positive(),
  confidence: z.enum(['medium', 'high']),
});
export type DealCallout = z.infer<typeof DealCalloutSchema>;

export const DealCalloutRequestSchema = z.object({
  listingId: z.coerce.number().int().positive(),
  /** `?deal_callout=` preview. A forced arm is shown but never counted. */
  force: DealCalloutArmSchema.optional(),
});
export type DealCalloutRequest = z.infer<typeof DealCalloutRequestSchema>;

export const DealCalloutResponseSchema = z.object({
  arm: DealCalloutArmSchema,
  /** True when this view counts in the test. */
  inTest: z.boolean(),
  forced: z.boolean(),
  /** Null when the listing does not qualify; the arm is then always `none`. */
  callout: DealCalloutSchema.nullable(),
});
export type DealCalloutResponse = z.infer<typeof DealCalloutResponseSchema>;

// ============================================================================
// Admin: the switch and the results, on the Orders dash
// ============================================================================

export const DealCalloutAbBodySchema = z.object({
  enabled: z.boolean(),
});

export const DealCalloutArmResultSchema = z.object({
  arm: DealCalloutArmSchema,
  /** Distinct people who viewed a qualifying listing in this arm. */
  people: z.number(),
  /** Of those, how many opened checkout (Buy Now) for a listing they were shown, after seeing it. */
  startedCheckout: z.number(),
  /** Of those, how many reached the payment step for it. */
  reachedPayment: z.number(),
  /** Of those, how many have an order for it. */
  paid: z.number(),
  /** startedCheckout / people, 0..1. Null with nobody in the arm. */
  checkoutRate: z.number().nullable(),
  /** Two-sided p-value of this arm's checkout rate against `none`. Null for `none` itself and for an empty arm. */
  pValueVsNone: z.number().nullable(),
});
export type DealCalloutArmResult = z.infer<typeof DealCalloutArmResultSchema>;

export const DealCalloutAbStateSchema = z.object({
  enabled: z.boolean(),
  updatedBy: z.string().nullable(),
  updatedAt: z.string().nullable(),
  since: z.string(),
  arms: z.array(DealCalloutArmResultSchema),
});
export type DealCalloutAbState = z.infer<typeof DealCalloutAbStateSchema>;
